import React, { useState, useEffect } from 'react';
import { 
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Loader2,
  Edit3,
  Save,
  Send,
  X,
  Download,
  Target
} from 'lucide-react';
import HeadlineSelectionModal from './HeadlineSelectionModal';

const V6System = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedMagazines, setExpandedMagazines] = useState(new Set());
  const [expandedArticles, setExpandedArticles] = useState(new Set());
  const [processingArticles, setProcessingArticles] = useState(new Set());
  const [generatedScripts, setGeneratedScripts] = useState({});
  const [articleStatuses, setArticleStatuses] = useState({});
  const [expandedScripts, setExpandedScripts] = useState({});
  const [editingScripts, setEditingScripts] = useState({});
  const [scriptEdits, setScriptEdits] = useState({});
  const [savingScripts, setSavingScripts] = useState(new Set());
  const [showCustomScriptModal, setShowCustomScriptModal] = useState(false);
  const [customScriptNotes, setCustomScriptNotes] = useState('');
  const [customScriptArticleId, setCustomScriptArticleId] = useState(null);
  const [isGeneratingCustomScript, setIsGeneratingCustomScript] = useState(false);
  
  // Headline selection state
  const [showHeadlineModal, setShowHeadlineModal] = useState(false);
  const [headlineModalData, setHeadlineModalData] = useState({ articleId: null, scriptIndex: null, scriptInfo: {} });
  const [availableHeadlines, setAvailableHeadlines] = useState([]);
  const [usedHeadlines, setUsedHeadlines] = useState([]);
  const [isSelectingHeadline, setIsSelectingHeadline] = useState(false);

  // Script regeneration state
  const [regeneratingScripts, setRegeneratingScripts] = useState(new Set());
  
  // Beats and headlines generation state
  const [generatingBeatsHeadlines, setGeneratingBeatsHeadlines] = useState(new Set());

  // Fetch all documents from all magazines with pre-processed script statuses
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents`);
      const data = await response.json();
      
      
      setDocuments(data.documents || []);
      
      // Process script statuses from the API response (no individual API calls needed)
      processScriptStatuses(data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Process script statuses from API response (replaces individual API calls)
  const processScriptStatuses = (docs) => {
    const statuses = {};
    const scripts = {};
    
    docs.forEach(doc => {
      const scriptStatus = doc.script_status;
      if (scriptStatus && scriptStatus.total > 0) {
        statuses[doc.id] = true;
        scripts[doc.id] = {
          success: true,
          scripts: scriptStatus.scripts,
          summary: {
            total: scriptStatus.total,
            ignored: scriptStatus.ignored,
            submitted: scriptStatus.submitted
          }
        };
      } else {
        statuses[doc.id] = false;
        scripts[doc.id] = null;
      }
    });
    
    setArticleStatuses(statuses);
    setGeneratedScripts(scripts);
  };

  // Toggle article expansion
  const toggleArticle = (articleId) => {
    setExpandedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  };

  // Toggle script expansion
  const toggleScript = (articleId, scriptIndex) => {
    setExpandedScripts(prev => {
      const key = `${articleId}-${scriptIndex}`;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  // Group documents by magazine
  const groupDocumentsByMagazine = () => {
    const grouped = {};
    documents.forEach(doc => {
      const magazine = doc.issue || 'Unknown';
      if (!grouped[magazine]) {
        grouped[magazine] = {
          magazine: magazine,
          documents: []
        };
      }
      grouped[magazine].documents.push(doc);
    });
    
    // Calculate magazine-level statistics
    Object.keys(grouped).forEach(magazine => {
      const magazineDocs = grouped[magazine].documents;
      let scriptsCount = 0;
      let ignoredCount = 0;
      let submittedCount = 0;
      
      magazineDocs.forEach(doc => {
        const scriptStatus = doc.script_status;
        if (scriptStatus && scriptStatus.total > 0) {
          scriptsCount++;
          if (scriptStatus.ignored > 0) ignoredCount++;
          if (scriptStatus.submitted > 0) submittedCount++;
        }
      });
      
      grouped[magazine].stats = {
        total: magazineDocs.length,
        scripts: scriptsCount,
        ignored: ignoredCount,
        submitted: submittedCount
      };
    });
    
    return grouped;
  };

  // Toggle magazine expansion
  const toggleMagazine = (magazine) => {
    setExpandedMagazines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(magazine)) {
        newSet.delete(magazine);
      } else {
        newSet.add(magazine);
      }
      return newSet;
    });
  };

  // Script editing functions
  const startEditingScript = (articleId, scriptIndex, currentContent) => {
    const key = `${articleId}-${scriptIndex}`;
    setEditingScripts(prev => ({
      ...prev,
      [key]: true
    }));
    setScriptEdits(prev => ({
      ...prev,
      [key]: currentContent
    }));
  };

  const cancelEditingScript = (articleId, scriptIndex) => {
    const key = `${articleId}-${scriptIndex}`;
    setEditingScripts(prev => ({
      ...prev,
      [key]: false
    }));
    setScriptEdits(prev => {
      const newEdits = { ...prev };
      delete newEdits[key];
      return newEdits;
    });
  };

  const updateScriptEdit = (articleId, scriptIndex, newContent) => {
    const key = `${articleId}-${scriptIndex}`;
    setScriptEdits(prev => ({
      ...prev,
      [key]: newContent
    }));
  };

  const saveEditedScript = async (articleId, scriptIndex) => {
    const key = `${articleId}-${scriptIndex}`;
    const editedContent = scriptEdits[key];
    
    if (!editedContent) return;

    try {
      setSavingScripts(prev => new Set(prev).add(key));

      // Call API to save edited script
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${articleId}/scripts/${scriptIndex}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editedContent,
          source: 'User'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save script: ${response.statusText}`);
      }

      // Update local state
      setEditingScripts(prev => ({
        ...prev,
        [key]: false
      }));

      // Refresh the script data
      await fetchDocuments();

    } catch (error) {
      console.error('Error saving script:', error);
      alert(`Error saving script: ${error.message}`);
    } finally {
      setSavingScripts(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

        // Note: submitScriptForProduction function removed - now handled by selectHeadlineAndSubmit

        const ignoreScript = async (articleId, scriptIndex) => {
          const key = `${articleId}-${scriptIndex}`;

          try {
            setSavingScripts(prev => new Set(prev).add(key));

            // Call API to mark script as ignored
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${articleId}/scripts/${scriptIndex}/ignore`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              }
            });

            if (!response.ok) {
              throw new Error(`Failed to ignore script: ${response.statusText}`);
            }

            // Refresh the script data
            await fetchDocuments();

          } catch (error) {
            console.error('Error ignoring script:', error);
            alert(`Error ignoring script: ${error.message}`);
          } finally {
            setSavingScripts(prev => {
              const newSet = new Set(prev);
              newSet.delete(key);
              return newSet;
            });
          }
        };

  // Preview PDF function
  const previewPDF = (storagePath) => {
    if (!storagePath) {
      alert('No PDF available for this article');
      return;
    }

    // Handle both old and new storage path formats
    let pdfUrl;
    if (storagePath.startsWith('pdfs/https://')) {
      // New format: pdfs/https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs/filename.pdf
      // Remove only the leading 'pdfs/' prefix
      pdfUrl = storagePath.substring(5); // Remove 'pdfs/' (5 characters)
      // Clean up malformed URLs (remove trailing ?)
      pdfUrl = pdfUrl.replace(/\?$/, '');
    } else {
      // Old format: pdfs/uploads/filename.pdf
      // Convert to Supabase public URL
      const fileName = storagePath.replace('pdfs/', '');
      pdfUrl = `https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs/${fileName}`;
    }

    // Open PDF in new tab with embedded viewer
    const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
    window.open(viewerUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
  };

  // Download PDF function
  const downloadPDF = async (storagePath, filename) => {
    if (!storagePath) {
      alert('No PDF available for this article');
      return;
    }
    
    try {
      // Handle both old and new storage path formats (same logic as previewPDF)
      let pdfUrl;
      if (storagePath.startsWith('pdfs/https://')) {
        // New format: pdfs/https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs/filename.pdf
        pdfUrl = storagePath.substring(5); // Remove 'pdfs/' (5 characters)
        pdfUrl = pdfUrl.replace(/\?$/, ''); // Clean up malformed URLs
      } else {
        // Old format: pdfs/uploads/filename.pdf
        const fileName = storagePath.replace('pdfs/', '');
        pdfUrl = `https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs/${fileName}`;
      }
      
      // Fetch the PDF file
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document.pdf';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  // Open custom script modal
  const openCustomScriptModal = (articleId) => {
    setCustomScriptArticleId(articleId);
    setCustomScriptNotes('');
    setShowCustomScriptModal(true);
  };

  // Close custom script modal
  const closeCustomScriptModal = () => {
    setShowCustomScriptModal(false);
    setCustomScriptNotes('');
    setCustomScriptArticleId(null);
  };

  // Generate custom script
  const generateCustomScript = async () => {
    if (!customScriptNotes.trim()) {
      alert('Please enter some notes for the custom script.');
      return;
    }

    try {
      setIsGeneratingCustomScript(true);

      // Call the custom script endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${customScriptArticleId}/custom-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notes: customScriptNotes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Request failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Custom script generation result:', result);

      // Close modal and refresh document statuses
      closeCustomScriptModal();
      await fetchDocuments();

    } catch (error) {
      console.error('Error generating custom script:', error);
      alert(`Error generating custom script: ${error.message}`);
    } finally {
      setIsGeneratingCustomScript(false);
    }
  };

  // Regenerate scripts function
  const regenerateScripts = async (articleId) => {
    try {
      setRegeneratingScripts(prev => new Set(prev).add(articleId));

      // Call the regenerate API endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${articleId}/regenerate-scripts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to regenerate scripts');
      }

      const result = await response.json();
      
      // Refresh the documents to show new scripts
      await fetchDocuments();
      
      alert(`Successfully regenerated ${result.scripts_count || 5} scripts for this article!`);

    } catch (error) {
      console.error('Error regenerating scripts:', error);
      alert(`Error regenerating scripts: ${error.message}`);
    } finally {
      setRegeneratingScripts(prev => {
        const newSet = new Set(prev);
        newSet.delete(articleId);
        return newSet;
      });
    }
  };

  // Headline selection functions
  const openHeadlineModal = async (articleId, scriptIndex, scriptInfo) => {
    try {
      setHeadlineModalData({ articleId, scriptIndex, scriptInfo });
      
      // Fetch available headlines for this article
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${articleId}/headlines`);
      if (!response.ok) {
        throw new Error('Failed to fetch headlines');
      }
      
      const data = await response.json();
      setAvailableHeadlines(data.headlines.map(h => h.text));
      setUsedHeadlines(data.headlines.filter(h => !h.available).map(h => h.text));
      setShowHeadlineModal(true);
      
    } catch (error) {
      console.error('Error fetching headlines:', error);
      alert(`Error loading headlines: ${error.message}`);
    }
  };

  const selectHeadlineAndSubmit = async (headline) => {
    try {
      setIsSelectingHeadline(true);
      
      // Step 1: Select the headline
      const selectResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${headlineModalData.articleId}/scripts/${headlineModalData.scriptIndex}/select-headline`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ headline })
        }
      );
      
      if (!selectResponse.ok) {
        const errorData = await selectResponse.json();
        throw new Error(errorData.detail || 'Failed to select headline');
      }
      
      // Step 2: Submit to production immediately
      const submitResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${headlineModalData.articleId}/scripts/${headlineModalData.scriptIndex}/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!submitResponse.ok) {
        const submitErrorData = await submitResponse.json();
        throw new Error(submitErrorData.detail || 'Failed to submit script');
      }
      
      // Close modal and refresh data
      setShowHeadlineModal(false);
      await fetchDocuments();
      
    } catch (error) {
      console.error('Error selecting headline and submitting:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSelectingHeadline(false);
    }
  };

  const handleScriptSubmit = async (articleId, scriptIndex, script) => {
    // Since scripts are submitted immediately after headline selection,
    // this function now only handles opening the headline modal
    const scriptInfo = {
      type: script.script_json?.type || 'Unknown Script',
      content: script.script_text
    };
    await openHeadlineModal(articleId, scriptIndex, scriptInfo);
  };

  // Extract text and generate script using V6 endpoint
  const extractAndGenerateScript = async (articleId) => {
    try {
      // Add to processing set
      setProcessingArticles(prev => new Set(prev).add(articleId));

      // Call the new V6 endpoint that handles everything
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${articleId}/extract-and-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Request failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('V6 extract and generate result:', result);

      // Refresh the document statuses to get the new scripts
      await fetchDocuments();

    } catch (error) {
      console.error('Error in extractAndGenerateScript:', error);
      alert(`Error: ${error.message}`);
    } finally {
      // Remove from processing set
      setProcessingArticles(prev => {
        const newSet = new Set(prev);
        newSet.delete(articleId);
        return newSet;
      });
    }
  };

  // Generate beats and headlines for a document
  const generateBeatsAndHeadlines = async (articleId) => {
    try {
      // Add to processing set
      setGeneratingBeatsHeadlines(prev => new Set(prev).add(articleId));

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/documents/${articleId}/generate-beats-headlines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Request failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Beats and headlines generation result:', result);

      // Show success message
      alert(`Beats and headlines generated successfully!\n\nBeats: ${result.beats_length} characters\nHeadlines: ${result.headlines_length} characters`);

      // Refresh the document statuses
      await fetchDocuments();

    } catch (error) {
      console.error('Error generating beats and headlines:', error);
      alert(`Error: ${error.message}`);
    } finally {
      // Remove from processing set
      setGeneratingBeatsHeadlines(prev => {
        const newSet = new Set(prev);
        newSet.delete(articleId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">V6 System</h1>
              <p className="text-sm text-gray-600">YPN207 Articles Only</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchDocuments}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">All Magazine Articles</h2>
            <div className="text-sm text-gray-500">
              {documents.length} documents
            </div>
          </div>

          {/* Documents List with Magazine Grouping */}
          <div className="bg-white rounded-lg shadow">
            {documents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {loading ? 'Loading documents...' : 'No documents found'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {Object.entries(groupDocumentsByMagazine()).map(([magazine, magazineData]) => {
                  const isMagazineExpanded = expandedMagazines.has(magazine);
                  return (
                    <div key={magazine}>
                      {/* Magazine Header - Clickable */}
                      <div 
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                        onClick={() => toggleMagazine(magazine)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="mr-3">
                              {isMagazineExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{magazine}</h3>
                              <p className="text-sm text-gray-500">{magazineData.documents.length} articles</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {magazineData.stats && (
                              <>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Scripts: {magazineData.stats.scripts}
                                </span>
                                {magazineData.stats.ignored > 0 && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Ignored: {magazineData.stats.ignored}
                                  </span>
                                )}
                                {magazineData.stats.submitted > 0 && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Submitted: {magazineData.stats.submitted}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Articles within magazine */}
                      {isMagazineExpanded && (
                        <div className="divide-y divide-gray-100">
                          {magazineData.documents.map((doc) => {
                  const isExpanded = expandedArticles.has(doc.id);
                  const isProcessing = processingArticles.has(doc.id);
                  const hasGeneratedScript = generatedScripts[doc.id];
                  return (
                    <div key={doc.id}>
                      {/* Article Header - Clickable */}
                      <div 
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleArticle(doc.id)}
                      >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center flex-1">
                                <div className="mr-2">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900">{doc.title}</h5>
                                  <p className="text-xs text-gray-500">
                                    {doc.source_meta?.author || 'Unknown Author'} • {new Date(doc.updated_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
              <div className="flex items-center space-x-3">
                {articleStatuses[doc.id] && (() => {
                  const scriptData = generatedScripts[doc.id];
                  const summary = scriptData?.summary;
                  return summary && (
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        Total: {summary.total}
                      </span>
                      {summary.ignored > 0 && (
                        <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                          Ignored: {summary.ignored}
                        </span>
                      )}
                      {summary.submitted > 0 && (
                        <span className="bg-green-100 px-2 py-1 rounded text-green-700">
                          Submitted: {summary.submitted}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
                            </div>
                      </div>
                      
                      {/* Expandable Content */}
                      {isExpanded && (
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                          <div className="ml-6 space-y-4">
                            {/* Preview PDF Buttons */}
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => previewPDF(doc.storage_path)}
                                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Preview PDF
                              </button>
                              
                              {/* Direct PDF Link */}
                              <button
                                onClick={() => {
                                  if (doc.storage_path) {
                                    let pdfUrl;
                                    if (doc.storage_path.startsWith('pdfs/https://')) {
                                      pdfUrl = doc.storage_path.substring(5).replace(/\?$/, '');
                                    } else {
                                      const fileName = doc.storage_path.replace('pdfs/', '');
                                      pdfUrl = `https://gvzakaegbycuawdvigap.supabase.co/storage/v1/object/public/pdfs/${fileName}`;
                                    }
                                    window.open(pdfUrl, '_blank');
                                  }
                                }}
                                className="inline-flex items-center px-3 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
                              >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Open Direct
                              </button>
                              
                              {/* Download PDF */}
                              <button
                                onClick={() => downloadPDF(doc.storage_path, doc.filename)}
                                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download PDF
                              </button>
                            </div>

                                {/* Script Generation Buttons */}
                                <div className="border-t border-gray-300 pt-3">
                                  <div className="flex items-center space-x-3">
                                    <button
                                      onClick={() => extractAndGenerateScript(doc.id)}
                                      disabled={isProcessing || articleStatuses[doc.id]}
                                      className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        articleStatuses[doc.id] 
                                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                                          : isProcessing 
                                            ? 'bg-orange-600 text-white cursor-not-allowed' 
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                      }`}
                                    >
                                      {isProcessing ? (
                                        <>
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          Processing...
                                        </>
                                      ) : articleStatuses[doc.id] ? (
                                        <>
                                          <FileText className="w-4 h-4 mr-2" />
                                          Script Already Generated
                                        </>
                                      ) : (
                                        <>
                                          <FileText className="w-4 h-4 mr-2" />
                                          Extract Text & Generate Script
                                        </>
                                      )}
                                    </button>
                                    
                                    <button
                                      onClick={() => openCustomScriptModal(doc.id)}
                                      disabled={!articleStatuses[doc.id]}
                                      className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        !articleStatuses[doc.id]
                                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                                          : 'bg-purple-600 text-white hover:bg-purple-700'
                                      }`}
                                    >
                                      <Edit3 className="w-4 h-4 mr-2" />
                                      Custom Script
                                    </button>
                                    
                                    <button
                                      onClick={() => regenerateScripts(doc.id)}
                                      disabled={!articleStatuses[doc.id] || regeneratingScripts.has(doc.id)}
                                      className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        !articleStatuses[doc.id] || regeneratingScripts.has(doc.id)
                                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                                          : 'bg-orange-600 text-white hover:bg-orange-700'
                                      }`}
                                    >
                                      {regeneratingScripts.has(doc.id) ? (
                                        <>
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          Regenerating...
                                        </>
                                      ) : (
                                        <>
                                          <FileText className="w-4 h-4 mr-2" />
                                          Regenerate Scripts
                                        </>
                                      )}
                                    </button>
                                    
                                    <button
                                      onClick={() => generateBeatsAndHeadlines(doc.id)}
                                      disabled={!articleStatuses[doc.id] || generatingBeatsHeadlines.has(doc.id)}
                                      className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        !articleStatuses[doc.id] || generatingBeatsHeadlines.has(doc.id)
                                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                                          : 'bg-blue-600 text-white hover:bg-blue-700'
                                      }`}
                                    >
                                      {generatingBeatsHeadlines.has(doc.id) ? (
                                        <>
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                          Generating...
                                        </>
                                      ) : (
                                        <>
                                          <Target className="w-4 h-4 mr-2" />
                                          Generate Beats & Headlines
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>

                                {/* Generated Script Display */}
                                {(hasGeneratedScript || articleStatuses[doc.id]) && (
                              <div className="border-t border-gray-300 pt-3">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Generated Scripts:</h4>
                                <div className="space-y-3">
                                  {(() => {
                                    const scriptData = generatedScripts[doc.id];
                                    const scripts = scriptData ? scriptData.scripts : [];
                                    
                                    return scripts.map((script) => {
                                      const scriptKey = `${doc.id}-${script.script_index}`;
                                      const isScriptExpanded = expandedScripts[scriptKey];
                                      
                                      return (
                                        <div key={script.script_index} className="bg-white border border-gray-200 rounded-lg">
                              {/* Script Header - Clickable */}
                              <div 
                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                                onClick={() => toggleScript(doc.id, script.script_index)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="mr-2">
                                      {isScriptExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                      )}
                                    </div>
                                    <h5 className="text-sm font-medium text-gray-900">
                                      Script {script.script_index}: {script.script_json?.type || `Script ${script.script_index}`}
                                    </h5>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                      {script.source === 'User' ? 'Edited' : 'AI Generated'}
                                    </span>
                                    {script.production && (
                                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                        Production Ready
                                      </span>
                                    )}
                                    {script.ignore && (
                                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                        🚫 Ignored
                                      </span>
                                    )}
                                    {script.selected_headline && (
                                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                        📰 {script.selected_headline}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                                          
                                          {/* Script Content */}
                                          {isScriptExpanded && (
                                            <div className="px-4 py-3">
                                              {(() => {
                                                const scriptKey = `${doc.id}-${script.script_index}`;
                                                const isEditing = editingScripts[scriptKey];
                                                const isSaving = savingScripts.has(scriptKey);
                                                const currentContent = scriptEdits[scriptKey] || script.script_text;
                                                
                                                return (
                                                  <div className="space-y-3">
                                                    {/* Script Content */}
                                                    <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                                                      {isEditing ? (
                                                        <textarea
                                                          value={currentContent}
                                                          onChange={(e) => updateScriptEdit(doc.id, script.script_index, e.target.value)}
                                                          className="w-full h-64 p-2 border-0 rounded-lg text-sm leading-relaxed resize-none focus:ring-2 focus:ring-blue-500"
                                                          placeholder="Edit script content..."
                                                        />
                                                      ) : (
                                                        <pre className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                                          {currentContent}
                                                        </pre>
                                                      )}
                                                    </div>
                                                    
                                        {/* Action Buttons */}
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-2">
                                            {!isEditing ? (
                                              <>
                                                <button
                                                  onClick={() => startEditingScript(doc.id, script.script_index, script.script_text)}
                                                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                                >
                                                  <Edit3 className="w-4 h-4 mr-1" />
                                                  Edit
                                                </button>
                                                <button
                                                  onClick={() => handleScriptSubmit(doc.id, script.script_index, script)}
                                                  disabled={isSaving || script.ignore || script.production}
                                                  className={`inline-flex items-center px-3 py-1.5 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    script.production 
                                                      ? 'bg-gray-500' 
                                                      : 'bg-blue-600 hover:bg-blue-700'
                                                  }`}
                                                >
                                                  {isSaving ? (
                                                    <>
                                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                      Processing...
                                                    </>
                                                  ) : script.production ? (
                                                    <>
                                                      <Send className="w-4 h-4 mr-1" />
                                                      Submitted
                                                    </>
                                                  ) : (
                                                    <>
                                                      <FileText className="w-4 h-4 mr-1" />
                                                      Select Headline & Submit
                                                    </>
                                                  )}
                                                </button>
                                                <button
                                                  onClick={() => ignoreScript(doc.id, script.script_index)}
                                                  disabled={isSaving || script.production}
                                                  className="inline-flex items-center px-3 py-1.5 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                  {isSaving ? (
                                                    <>
                                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                      Ignoring...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <EyeOff className="w-4 h-4 mr-1" />
                                                      Ignore
                                                    </>
                                                  )}
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  onClick={() => saveEditedScript(doc.id, script.script_index)}
                                                  disabled={isSaving}
                                                  className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                  {isSaving ? (
                                                    <>
                                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                      Saving...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <Save className="w-4 h-4 mr-1" />
                                                      Save
                                                    </>
                                                  )}
                                                </button>
                                                <button
                                                  onClick={() => cancelEditingScript(doc.id, script.script_index)}
                                                  className="inline-flex items-center px-3 py-1.5 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
                                                >
                                                  <X className="w-4 h-4 mr-1" />
                                                  Cancel
                                                </button>
                                              </>
                                            )}
                                          </div>
                                          
                                        </div>
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Script Modal */}
      {showCustomScriptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Generate Custom Script</h3>
              <button
                onClick={closeCustomScriptModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <label htmlFor="custom-notes" className="block text-sm font-medium text-gray-700 mb-2">
                Custom Notes
              </label>
              <textarea
                id="custom-notes"
                value={customScriptNotes}
                onChange={(e) => setCustomScriptNotes(e.target.value)}
                placeholder="Enter your custom notes to help focus the script content..."
                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={closeCustomScriptModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generateCustomScript}
                disabled={isGeneratingCustomScript || !customScriptNotes.trim()}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isGeneratingCustomScript || !customScriptNotes.trim()
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {isGeneratingCustomScript ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Custom Script
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Headline Selection Modal */}
      <HeadlineSelectionModal
        isOpen={showHeadlineModal}
        onClose={() => setShowHeadlineModal(false)}
        onSelect={selectHeadlineAndSubmit}
        headlines={availableHeadlines}
        usedHeadlines={usedHeadlines}
        scriptInfo={headlineModalData.scriptInfo}
        loading={isSelectingHeadline}
      />

    </div>
  );
};

export default V6System;
